import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Rocket, Mail, Clock } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  { value: 'active', label: 'Active', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
];

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const BetaInquiriesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingNote, setEditingNote] = useState({}); // {id: 'draft note'}

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/beta-inquiries`);
      setItems(r.data || []);
    } catch (e) {
      toast.error('Could not load inquiries');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/beta-inquiries/${id}`, { status });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update status');
    }
  };

  const saveNote = async (id) => {
    const note = editingNote[id] ?? '';
    try {
      await axios.put(`${API}/admin/beta-inquiries/${id}`, { admin_note: note });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, admin_note: note } : i)));
      setEditingNote((s) => { const c = { ...s }; delete c[id]; return c; });
      toast.success('Note saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save note');
    }
  };

  const filtered = items.filter((i) => filter === 'all' || (['beta', 'pilot', 'partner'].includes(filter) ? i.kind === filter : i.status === filter));

  const counts = {
    all: items.length,
    new: items.filter((i) => i.status === 'new').length,
    beta: items.filter((i) => i.kind === 'beta').length,
    pilot: items.filter((i) => i.kind === 'pilot').length,
    partner: items.filter((i) => i.kind === 'partner').length,
  };

  return (
    <div className="space-y-4" data-testid="beta-inquiries-panel">
      <div className="flex flex-wrap items-center gap-2">
        {[
          { k: 'all', label: `All (${counts.all})` },
          { k: 'new', label: `New (${counts.new})` },
          { k: 'beta', label: `Beta (${counts.beta})` },
          { k: 'pilot', label: `Pilot (${counts.pilot})` },
          { k: 'partner', label: `Organizations (${counts.partner})` },
          { k: 'contacted', label: 'Contacted' },
          { k: 'active', label: 'Active' },
          { k: 'closed', label: 'Closed' },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === f.k ? 'bg-cyan-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-foreground hover:bg-slate-200'
            }`}
            data-testid={`inquiry-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={load} data-testid="inquiry-refresh">Refresh</Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="surface-card rounded-2xl p-8 text-center">
          <Rocket className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No inquiries {filter !== 'all' ? `in "${filter}"` : 'yet'}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => {
            const meta = statusMeta(i.status);
            const kindBadge = i.kind === 'pilot' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' : i.kind === 'partner' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200';
            const noteDraft = editingNote[i.id];
            return (
              <div key={i.id} className="surface-card rounded-2xl p-4 space-y-3" data-testid={`inquiry-${i.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground">{i.name}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${kindBadge}`}>{i.kind}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Mail className="w-3 h-3" />
                      <a href={`mailto:${i.email}`} className="hover:underline">{i.email}</a>
                      {i.org && <> · <span>{i.org}</span></>}
                    </p>
                    {i.area_label && <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300 mt-0.5">Interest: {i.area_label}</p>}
                    <p className="text-[11px] text-muted-foreground/70 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" /> {fmtDate(i.created_at)}
                    </p>
                  </div>
                </div>

                {i.note && (
                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Their note</p>
                    <p className="text-sm text-foreground whitespace-pre-line mt-1">{i.note}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5">
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => updateStatus(i.id, o.value)}
                      className={`text-[11px] px-2 py-1 rounded-lg font-medium transition-colors ${
                        i.status === o.value ? 'bg-cyan-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-foreground hover:bg-slate-200'
                      }`}
                      data-testid={`inquiry-${i.id}-status-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <a
                    href={`mailto:${i.email}?subject=Re: your ${i.kind} inquiry — CircleUpSquad`}
                    className="text-[11px] px-2 py-1 rounded-lg font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200"
                    data-testid={`inquiry-${i.id}-reply`}
                  >
                    Reply
                  </a>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Admin note</p>
                  {noteDraft !== undefined ? (
                    <div className="space-y-2">
                      <Textarea value={noteDraft} onChange={(e) => setEditingNote((s) => ({ ...s, [i.id]: e.target.value }))} className="rounded-lg text-sm resize-none" rows={2} data-testid={`inquiry-${i.id}-note-input`} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveNote(i.id)} className="btn-primary" data-testid={`inquiry-${i.id}-note-save`}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingNote((s) => { const c = { ...s }; delete c[i.id]; return c; })}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingNote((s) => ({ ...s, [i.id]: i.admin_note || '' }))}
                      className="text-xs text-foreground hover:text-cyan-600 text-left w-full transition-colors"
                      data-testid={`inquiry-${i.id}-note-edit`}
                    >
                      {i.admin_note ? <span className="whitespace-pre-line">{i.admin_note}</span> : <span className="text-muted-foreground italic">Add a note…</span>}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BetaInquiriesPanel;
